import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '../../app/libs/cn'

// dropdown เลือกค่าแบบมีช่องค้นหา (พิมพ์กรองได้) — ใช้แทน <select> ธรรมดาที่รายการสีจางและค้นหาไม่ได้ (จังหวัด/เขต/แขวง ฯลฯ)
// ค่า value/onChange เป็นข้อความเดียวกับ <select> เดิม (ชื่อที่เลือก, '' = ยังไม่เลือก) จึงสลับมาใช้แทนได้ตรงๆ
// คีย์บอร์ด: ↑↓ เลื่อน, Enter เลือก, Esc ปิด — คลิกนอกกล่องปิดเอง
export default function SearchableSelect({
  value, onChange, options, placeholder = 'เลือก...', searchPlaceholder = 'พิมพ์เพื่อค้นหา...',
  disabled, loading, emptyText = 'ไม่พบรายการที่ค้นหา', className, id,
}: {
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  loading?: boolean
  emptyText?: string
  className?: string   // class ของปุ่มหลัก (ส่ง inputClass ของหน้านั้นมาให้หน้าตาเหมือนช่องกรอกอื่น)
  id?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options
  }, [options, query])

  const close = () => { setOpen(false); setQuery('') }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) close() }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // เปิดแล้วโฟกัสช่องค้นหา + เลื่อนไปที่ค่าที่เลือกอยู่
  useEffect(() => {
    if (!open) return
    searchRef.current?.focus()
    const idx = Math.max(options.indexOf(value), 0)
    setActive(idx)
    requestAnimationFrame(() => {
      (listRef.current?.children[idx] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const choose = (v: string) => { onChange(v); close() }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next = Math.min(Math.max(active + (e.key === 'ArrowDown' ? 1 : -1), 0), Math.max(filtered.length - 1, 0))
      setActive(next)
      ;(listRef.current?.children[next] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' })
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[active] !== undefined) choose(filtered[active])
    }
  }

  const label = loading ? 'กำลังโหลด...' : value || placeholder

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex items-center justify-between gap-2 text-left',
          className,
          open && 'border-indigo-400',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={cn('truncate', !value && 'text-text-muted/70')}>{label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-sm border border-border bg-white shadow-[0_12px_32px_rgba(25,23,42,0.16)]">
          <div className="relative border-b border-border">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0) }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm text-text outline-none placeholder:text-text-muted/70"
            />
          </div>
          <ul ref={listRef} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-text-muted">{emptyText}</li>
            ) : filtered.map((o, i) => {
              const selected = o === value
              return (
                <li
                  key={o}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(o)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-2 px-4 py-2 text-sm text-text',
                    i === active && 'bg-accent/10',
                    selected && 'font-semibold text-accent',
                  )}
                >
                  <span className="truncate">{o}</span>
                  {selected && <Check className="h-4 w-4 shrink-0" />}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
